import { Button, Card, CardContent, Chip, IconButton, Link, Tooltip, Typography } from "@mui/material";
import { Box, Stack } from "@mui/system";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { EntityTypeForFollow, FacilityTypeEnum, OrderReasonEnum, OrderReasonPickUpEnum, deliveryOrderType, equipmentOrderType, quoteStatus, statusEnum } from "../../../common/enums";
import CancelOrder from "../../../components/CancelOrder/CancelOrder";
import { errorToast, successToast } from "../../../components/ToastMessage/ToastMessage";
import FollowButton from "../../../components/followButton/followButton";
import withTitle from "../../../components/withTitle/withTitle";
import { GenerateReceiptApi, OrderFeedbackApi, OrdersComponentApi } from "../../../constants/ApiConstants";
import { HTTPMethod, callApi as OrderDetailRequest } from "../../../services/HttpService/HttpService";
import styles from "./OrdersDetails.module.scss";
import StatusBadge from "../../../components/StatusBadge/StatusBadge";
import ReasonCode from "../../../common/Utils/ReasonCode/ReasonCode";
import Demographics from "../../../components/Demographics/Demographics";
import { useSelector } from "react-redux";
import PriorityIcon from "../../../common/Utils/PriorityIcon/PriorityIcon/PriorityIcon";
import ETA from "../../../components/ETA/ETA";
import MapLocationButton from "../../../components/MapLocationButton/MapLocationButton";
import OrderTypeText from "../../../common/Utils/OrderTypeText/OrderTypeText";
import { handlePrimaryContact } from "../../../common/Utils/CommonFunctions/CommonFunctions";
import ProofOrderDetailCards from "../../../components/ProofOrderDetailCards/ProofOrderDetailCards";
import OrderType from "../../../components/OrderType/OrderType";
import ItemLabel from "../../../components/ItemLabel/ItemLabel";
import ClinicalInformationPanel from "../../../components/ClinicalInformationPanel/ClinicalInformationPanel";
import ContactsPanel from "../../../components/ContactsPanel/ContactsPanel";
import Panel from "../../../components/Panel/Panel";
import AddressPanel from "../../../components/AddressPanel/AddressPanel";
import { CustomFieldDetails } from "../../../common/customFields/CustomFieldDetails";
import { handleOrderCustomFields } from "../../../common/CustomFieldHelperFunctions/CustomFieldHelperFunctions";
import QuoteCard from "../QuoteCard/QuoteCard";
import OrdersDetailsSkeleton from "./OrdersDetailsSkeleton";
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import Icon from "../../../components/Icon/Icon";

export const getOrderFeedback = async () => {
  const orderId = location.pathname.split("/").pop();
  const orderFeedbackResponse = await OrderDetailRequest(HTTPMethod.Get, OrderFeedbackApi.orderFeedbackPageAuth.replace("{order_id}", orderId))
  if (orderFeedbackResponse && orderFeedbackResponse.status_code == 200) {
    return orderFeedbackResponse.data
  }
}

export const checkOrderFeedbackExpired = (createdOn, orderFeedBackExpirationDays) => {
  const createdDate = new Date(createdOn);
  const expiredDate = new Date(createdDate.setDate(createdDate.getDate() + orderFeedBackExpirationDays));
  const currentDate = new Date();
  return expiredDate.getTime() >= currentDate.getTime();
}

const OrdersDetails = ({ setOrder, setOrderPatientId }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isFollowButton, setFollowButton] = useState(false);
  const [isFollowFollowing, setSelectedFollowFollowing] = useState(false);
  const [orderDetailsData, setOrdersDetailsData] = useState(null);
  const [patientContacts, setPatientContacts] = useState([])
  const [proofOfDelivery, setProofOfDelivery] = useState(null);
  const navigate = useNavigate();
  const [isCancelOrderPopupOpen, setIsCancelOrderPopupOpen] = useState(false);
  const [alertEquipmentName, setAlertEquipmentName] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [orderFeedbackData, setOrderFeedbackData] = useState();
  const [orderCustomFields, setOrderCustomFields] = useState([]);
  const orderFeedBackExpirationDays = useSelector((state) => state.authState.userData?.data?.application_settings?.order_feed_back_expiration_days)
  const companyId = useSelector((state) => state.authState.userData?.data?.company_settings[0]?.company_id ?? 0);
  const surveyLinkWithAuthForUser = useSelector((state) => state.authState.userData?.data?.application_settings?.survey_link_with_auth_for_user);
  const userRoles = useSelector(state => state.authState.userData?.data?.user_info?.role);
  const isReadOnly = userRoles?.is_read_only ? true : false;


  const handleRecurringDetailPage = (recurring_order_id) => {
    navigate("/recurring-orders/recurring-order-details/" + recurring_order_id);
  };

  const handleOrdersDetails = async () => {
    setOrder(null);
    setOrdersDetailsData(null);
    setIsLoading(true);
    const orderId = location.pathname.split("/").pop();
    const response = await OrderDetailRequest(
      HTTPMethod.Get, `${OrdersComponentApi.orderDetails}/${orderId}?cwp_order_detail=true`
    );
    if (response && response.status_code == 200 && response.data) {
      setOrdersDetailsData(response.data);
      //console.log(response.data);
      sessionStorage.setItem("reasonCode", response?.data?.reason_code);
      const data = handlePrimaryContact(response.data?.patient_contacts);
      setPatientContacts([...data]);

      setOrder(response.data?.orders?.order_id);
      setOrderPatientId(response.data?.patient_info?.patient_id)
      setSelectedFollowFollowing(response?.data?.orders?.follow)
      setIsLoading(false);
      if (
        response.data?.orders?.order_status_id.includes(deliveryOrderType.Delivery) ||
        response.data?.orders?.order_status_id.includes(deliveryOrderType.PickUp) ||
        response.data?.order_type?.toLowerCase().includes("service")
      )
        proofOfDeliveryDetails();
      if (
        response.data?.orders?.order_complete_status !== "Complete" &&
        response.data?.orders?.order_complete_status !== "Delivered" &&
        response.data?.orders?.order_complete_status !== "Canceled"
      )
        setFollowButton(true);
    }
  };

  const proofOfDeliveryDetails = async () => {
    const orderId = location.pathname.split("/").pop();
    const response = await OrderDetailRequest(
      HTTPMethod.Get,
      OrdersComponentApi.orderProofOfDelivery.replace("{order_id}", orderId)
    );
    if (response && response.status_code == 200 && response.data) {
      setProofOfDelivery(response.data);
    }
  };
  useEffect(() => {
    handleOrdersDetails();
  }, [location.pathname]);

  const handlePdfGenerate = async () => {
    setIsLoading(true)
    const orderId = location.pathname.split("/").pop();
    const pdfAPIResponse = await OrderDetailRequest(HTTPMethod.Get, GenerateReceiptApi.generateReceipt + "?order_id=" + orderId)
    if (pdfAPIResponse && pdfAPIResponse.status_code == 200) {
      if (pdfAPIResponse.data) {
        setIsLoading(false)
        const pdfCreateUrl = pdfAPIResponse.data.receipt_order_url;
        window.open(pdfCreateUrl)
      }
    }
  }

  const handleReportAnIssue = () => {
    navigate('/support/report-an-issue/', {
      state: {
        prefill: {
          location: {
            location_id: orderDetailsData?.patient_info?.branch_id,
            location_name: orderDetailsData?.hospice_branch_contact?.hospice_branch_name,
          },
          patient: {
            patient_id: orderDetailsData?.patient_info?.patient_id,
            patient_full_name: `${orderDetailsData?.patient_info?.patient_last_name}, ${orderDetailsData?.patient_info?.patient_first_name} ${orderDetailsData?.patient_info?.patient_middle_initial ?? ''}`?.trim(),
            patient_status: orderDetailsData?.patient_info?.patient_status,
          },
          order: {
            order_id: orderDetailsData?.orders?.order_id,
          },
        },
      },
    });
  }


  const handleEditOrder = () => {
    localStorage.removeItem('NewOrder');
    navigate(`/orders/edit-order/${orderDetailsData?.orders?.order_id}`);
  };

  const getFeedbackData = async () => {
    const feedbackResponse = await getOrderFeedback();
    setOrderFeedbackData(feedbackResponse);
  }

  useEffect(() => {
    if (surveyLinkWithAuthForUser) {
      getFeedbackData();
    }
  }, [surveyLinkWithAuthForUser])

  useEffect(() => {
    if (orderDetailsData?.orders?.order_id && companyId)
      handleOrderCustomFields(setOrderCustomFields, orderDetailsData?.patient_info.branch_id, companyId, orderDetailsData?.orders?.order_id)
  }, [orderDetailsData?.orders?.order_id, companyId])

  const handleCancelOrder = async () => {
    setIsCancelOrderPopupOpen(false)
    const requestBody = {
      order_id: alertEquipmentName,
      cancel_reason: cancelReason
    }
    const response = await OrderDetailRequest(
      HTTPMethod.Put,
      OrdersComponentApi.cancelOrder,
      requestBody,
      "event-cancel-order"
    );
    setCancelReason("");
    if (response && response.status_code == 200) {
      const message = response?.return_message[0];
      successToast(message);
      handleOrdersDetails();
    }
    else {
      errorToast("There was a problem. Please contact technical support.");
    }
  }

  const formatScheduledRequest = (start, end) => {
    if (!start || !end) return null;

    const formatDateTime = (dateTime, includeDate = true) => {
      if (!dateTime) return "";

      const [date, time, meridian] = dateTime.replace(/[()]/g, "").split(" ");
      const [hours, minutes] = time.split(":");

      const formattedTime = `${hours}:${minutes} ${meridian}`;
      return includeDate ? `${date} (${formattedTime})` : `${formattedTime}`;
    };

    const [startDate] = start.replace(/[()]/g, "").split(" ");
    const [endDate] = end.replace(/[()]/g, "").split(" ");

    if (startDate === endDate) {
      return `${startDate} (${formatDateTime(start, false)} - ${formatDateTime(end, false)})`;
    }

    return `${formatDateTime(start)} - ${formatDateTime(end)}`;
  };

  return (
    <>
      {orderDetailsData ?
        <Stack spacing={6} sx={{ pb: 5 }}>
          <Card>
            <CardContent >
              <Stack
                flexDirection={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
              >
                <Typography variant="h4">
                  {orderDetailsData?.orders?.order_id}
                </Typography>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems={{ sm: "flex-start" }}
                  justifyContent={{
                    xs: "flex-start",
                    sm: "space-between",
                  }}
                >
                  {!(
                    orderDetailsData?.orders?.order_complete_status ==
                    "Canceled" ||
                    orderDetailsData?.orders?.order_complete_status ==
                    "Complete" ||
                    orderDetailsData?.orders?.order_complete_status ==
                    "Delivered" ||
                    orderDetailsData?.orders?.order_complete_status ==
                    "Picked Up"
                  ) &&
                    isFollowButton && (
                      <FollowButton
                        toggleValue={isFollowFollowing}
                        toggleSetValue={setSelectedFollowFollowing}
                        followPayload={{
                          entityId: orderDetailsData?.orders?.order_id,
                          userId: [],
                          entityType: EntityTypeForFollow.Order,
                        }}
                      />
                    )}
                </Stack>
              </Stack>
              <Stack sx={{ gap: 6, mt: 3 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ sm: "center" }}
                  justifyContent="space-between"
                  spacing={2}
                  sx={{ mt: 1 }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    {orderDetailsData?.orders?.order_complete_status && (
                    <StatusBadge
                        status={orderDetailsData?.orders?.order_complete_status}
                        label={
                          orderDetailsData?.orders?.order_complete_status === "Canceled" &&
                            orderDetailsData?.orders?.order_type === "Delivery"
                            ? "Delivery Canceled"
                            : orderDetailsData?.orders?.order_complete_status === "Canceled" &&
                              orderDetailsData?.orders?.order_type === "Pickup"
                              ? "Pickup Canceled"
                              : orderDetailsData?.orders?.order_complete_status
                        }
                      />
                    )}
                    {[statusEnum["En-Route"], statusEnum["On my way"], statusEnum["Picking Up"]].includes(orderDetailsData?.orders?.order_complete_status) && (
                      <ETA className={styles.etaIcon} arrivalTime={orderDetailsData?.orders?.arrival_time} estimatedArrivalTime={orderDetailsData?.orders?.estimated_arrival_time} orderStatus={orderDetailsData?.orders?.order_complete_status} exceptedArrivalTime={orderDetailsData?.orders?.excepted_arrival_time} exceptedExitTime={orderDetailsData?.orders?.excepted_exit_time} siteLocalTime={orderDetailsData?.orders?.site_local_time} isDelayed={orderDetailsData?.orders?.is_eta_delayed} />
                    )}
                  </Stack>
                  <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
                    {orderDetailsData.order_status !== "Canceled" && (
                      <>
                        {orderDetailsData.is_view_receipt_link_visible ===
                          true &&
                          orderDetailsData.receipt_link_name ===
                          "Work order" && (
                            <Tooltip
                              title="Work Order"
                              onClick={handlePdfGenerate} data-testid="pdfGenerateButton"
                            >
                              <IconButton color="primary">
                                <Icon icon="fa-receipt" />
                              </IconButton>
                            </Tooltip>
                          )}

                        {orderDetailsData.is_view_receipt_link_visible ===
                          true &&
                          orderDetailsData.receipt_link_name ===
                          "View Receipt" && (
                            <Tooltip
                              title="View Receipt"
                              onClick={handlePdfGenerate}
                            >
                              <IconButton color="primary">
                                <Icon icon="fa-receipt" />
                              </IconButton>
                            </Tooltip>
                          )}
                      </>
                    )}
                    {orderDetailsData?.orders?.order_id &&
                      (orderDetailsData?.orders?.order_complete_status == "Received") && !isReadOnly && (
                        <Tooltip
                          title="Edit Order"
                        >
                          <IconButton
                            color="secondary"
                            data-testid="EditOrder"
                            onClick={handleEditOrder}
                          >
                            <Icon icon="fa-pencil" />
                          </IconButton>
                        </Tooltip>
                      )}
                    {orderDetailsData?.orders?.order_id && (orderDetailsData?.orders?.order_complete_status == "Received" || orderDetailsData?.orders?.order_complete_status == "Pending Approval" || orderDetailsData?.orders?.order_complete_status == quoteStatus.providerQuote) && !isReadOnly && (
                      <Tooltip
                        title="Cancel Order"
                      >
                        <IconButton
                          color="error"
                          onClick={(e) => {
                            setIsCancelOrderPopupOpen(true);
                            setAlertEquipmentName(
                              orderDetailsData?.orders?.order_id
                            );
                            e.stopPropagation();
                          }}
                        >
                          <Icon icon="fa-xmark" />
                        </IconButton>
                      </Tooltip>
                    )}

                    {(orderDetailsData?.orders?.order_complete_status === "Delivered" || orderDetailsData?.orders?.order_complete_status === "Picked Up") && (
                      <Tooltip
                        title="Report an Issue"
                      >
                        <IconButton
                          color="warning"
                          data-testid="reportIssueBtn"
                          href="/support/report-an-issue/"
                          onClick={handleReportAnIssue}
                        >
                          <Icon icon="fa-flag" />
                        </IconButton>
                      </Tooltip>
                    )}

                    {(orderDetailsData?.orders?.order_complete_status === "En-Route" || orderDetailsData?.orders?.order_complete_status === "On my way" || orderDetailsData?.orders?.order_complete_status === "Picking Up") && (

                      <Box data-testid="EtaMapBtn" onClick={(event) => event.preventDefault()}>
                        <MapLocationButton orderStatus={orderDetailsData?.orders?.order_complete_status} orderId={orderDetailsData?.orders?.order_id}
                                                  latitude={orderDetailsData?.address?.latitude} longitude={orderDetailsData?.address?.longitude} addressLine1={orderDetailsData?.address?.address_line1} addressLine2={orderDetailsData?.address?.address_line2} city={orderDetailsData?.address?.city} state={orderDetailsData?.address?.state} zipcode={orderDetailsData?.address?.zip_code} country={orderDetailsData?.address?.country} arrivalTime={orderDetailsData?.orders?.arrival_time} estimatedArrivalTime={orderDetailsData?.orders?.estimated_arrival_time} exceptedArrivalTime={orderDetailsData?.orders?.excepted_arrival_time} exceptedExitTime={orderDetailsData?.orders?.excepted_exit_time} siteLocalTime={orderDetailsData?.orders?.site_local_time} isDelayed={orderDetailsData?.orders?.is_eta_delayed}
                        />
                      </Box>
                    )}
                  </Stack>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" spacing={2}>
                  <Stack spacing={1}>
                    <Typography variant="h5">
                      {orderDetailsData?.hospice_branch_contact?.hospice_branch_name}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <OrderTypeText
                        orderType={orderDetailsData?.orders?.order_type}
                        orderStatus={orderDetailsData?.orders?.order_complete_status}
                        deliveredOn={orderDetailsData?.formatted_delivered_on}
                        deliverBy={orderDetailsData?.deliver_by_formatted}
                        pickedUpOn={orderDetailsData?.delivered_on_formatted}
                        pickupBy={orderDetailsData.pickup_by_formatted}
                        servicedOn={orderDetailsData?.formatted_delivered_on}
                        serviceBy={orderDetailsData?.service_by_formatted}
                        mixOrderConfiguration={orderDetailsData?.orders?.mix_order_configuration}
                        scheduledForStart={orderDetailsData?.orders?.scheduled_start_date_formatted}
                        scheduledForEnd={orderDetailsData?.orders?.scheduled_end_date_formatted}
                      />
                      {orderDetailsData?.is_back_dated &&
                        <Tooltip title="Order was backdated when completed.">
                          <EventAvailableRoundedIcon style={{ color: "var(--color-orange" }} />  {/* TODO: Update icon */}
                        </Tooltip>}
                    </Stack>
                    <ItemLabel label="Provider" text={`${orderDetailsData?.provider_contacts?.provider_name} (${orderDetailsData?.provider_contacts?.provider_location})`} />
                  </Stack>
                  <Stack direction="row" sx={{ alignItems: "baseline", gap: 4 }}>
                    {PriorityIcon(orderDetailsData?.priority_name)}
                    {orderDetailsData?.reason_code &&
                      ReasonCode(orderDetailsData?.reason_code, orderDetailsData?.orders?.order_type)
                    }
                    {!([quoteStatus.providerQuote, quoteStatus.stateServeAudit, quoteStatus.clientReview].includes(orderDetailsData?.orders?.order_complete_status)) &&
                      <OrderType mixOrderConfiguration={orderDetailsData?.orders?.mix_order_configuration} />
                    }
                  </Stack>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
          {orderDetailsData?.patient_info && (
            <Demographics patientId={orderDetailsData?.patient_info?.patient_id} patientInformation={orderDetailsData?.patient_info} hasOtherOrder={orderDetailsData.has_other_order} orderId={orderDetailsData?.orders?.order_id}
              branchId={orderDetailsData?.patient_info.branch_id} />
          )}

          <ContactsPanel
            patientContacts={patientContacts}
            hospiceBranchContact={orderDetailsData?.hospice_branch_contact}
            providerContacts={orderDetailsData?.provider_contacts}
            technicianDetails={orderDetailsData?.technician_details}
          />

          <ClinicalInformationPanel patientInfo={orderDetailsData?.patient_info} />

          {orderDetailsData?.reason === "Quote" && <QuoteCard data={orderDetailsData} orderCustomFields={orderCustomFields} orderStatus={orderDetailsData?.orders?.order_complete_status} handleOrdersDetails={handleOrdersDetails} />}
          <AddressPanel
            address={orderDetailsData?.address}
            addressNotes={orderDetailsData?.address_notes}
          />
          <Panel title="Order Details" variant="outlined" data-testid='orderDetailsContainer'>
            <Stack spacing={1} data-testid='orderDetails'>
              {orderDetailsData?.total_equipment !== 0 && orderDetailsData?.total_equipment && (
                <ItemLabel label={t("Dashboard.uniqueEquipment")} text={orderDetailsData?.total_equipment} />
              )}
              {orderDetailsData?.orders?.parent_order_id > 0 && (
                <ItemLabel
                  label={t("Dashboard.refernceOrderId")}
                  text={
                    <Link to={`/orders/order-details/${orderDetailsData?.orders?.parent_order_id}`} className={styles.routerLink}>
                      {orderDetailsData?.orders?.parent_order_id}
                    </Link>
                  }
                />
              )}
              {orderDetailsData?.equipment_requiring_approval !== 0 && orderDetailsData?.equipment_requiring_approval && (
                <ItemLabel label="Equipment requiring approval" text={orderDetailsData?.equipment_requiring_approval} />
              )}
              {orderDetailsData?.formatted_ordered_on && (
                <ItemLabel label="Ordered on" text={orderDetailsData?.formatted_ordered_on} />
              )}
              {orderDetailsData?.ordered_by && (
                <ItemLabel label="Ordered by" text={`${orderDetailsData?.ordered_by} (${orderDetailsData?.ordered_by_company_name})`} />
              )}
              {orderDetailsData?.reason_code && (
                <ItemLabel label="Order reason" text={orderDetailsData?.orders?.order_type == "Pickup" ? OrderReasonPickUpEnum[orderDetailsData?.reason_code] : OrderReasonEnum[orderDetailsData?.reason_code]} />
              )}
              {orderDetailsData?.priority_name && (
                <ItemLabel label="Priority" text={orderDetailsData?.priority_name} />
              )}
              {orderDetailsData?.orders?.requested_date_formatted && !orderDetailsData?.orders?.scheduled_request_start_date_formatted && (
                <ItemLabel label="Requested Date" text={orderDetailsData?.orders?.requested_date_formatted.split(" ")[0]} />
              )}
              {orderDetailsData?.orders?.scheduled_request_start_date_formatted && (
                <ItemLabel label="Requested Schedule" text={formatScheduledRequest(orderDetailsData.orders.scheduled_request_start_date_formatted, orderDetailsData.orders.scheduled_request_end_date_formatted)} />
              )}
              {orderDetailsData?.orders?.reference && (
                <ItemLabel label="Reference" text={orderDetailsData?.orders?.reference} />
              )}
              {orderDetailsData?.patient_nurse_details?.length > 0 &&
                orderDetailsData?.patient_nurse_details.some(p => p.nurse_name !== null || p.nurse_phone !== null) && (
                  <ItemLabel
                    label="Associated nurse"
                    text={orderDetailsData.patient_nurse_details.map((patient) => (
                      patient?.nurse_name && (
                        <Tooltip
                          key={patient.nurse_name}
                          title={patient?.nurse_phone ? patient.nurse_phone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3") : ""}
                        >
                          <Chip label={patient.nurse_name} />
                        </Tooltip>
                      )
                    ))}
                  />
                )}
              {orderCustomFields?.length > 0 && <CustomFieldDetails customFieldData={orderCustomFields} />}
              {orderDetailsData?.recurring_order_id > 0 && (
                <ItemLabel
                  label="Recurring order"
                  text={
                    <Link data-testid="RecurringDetailPage" onClick={() => handleRecurringDetailPage(orderDetailsData?.recurring_order_id)}>
                      {orderDetailsData?.recurring_order_id}
                    </Link>
                  }
                />
              )}
              {orderDetailsData?.approval_info?.formatted_approved_on && (
                <ItemLabel label="Approved on" text={orderDetailsData?.approval_info?.formatted_approved_on} />
              )}
              {orderDetailsData?.approval_info?.approved_by && (
                <ItemLabel label="Approved by" text={orderDetailsData?.approval_info?.approved_by} />
              )}
              {orderDetailsData?.approval_info?.formatted_approval_rejected_on && (
                <ItemLabel label="Rejected on" text={orderDetailsData?.approval_info?.formatted_approval_rejected_on} />
              )}
              {orderDetailsData?.approval_info?.approval_rejected_by && (
                <ItemLabel label="Rejected by" text={orderDetailsData?.approval_info?.approval_rejected_by} />
              )}
              {orderDetailsData?.first_acknowledged && (
                <ItemLabel label="Acknowledged" text={orderDetailsData.first_acknowledged} />
              )}
              <Stack direction="row" spacing={1} alignItems="center">
                <OrderTypeText
                  orderType={orderDetailsData?.orders?.order_type}
                  orderStatus={orderDetailsData?.orders?.order_complete_status}
                  deliveredOn={orderDetailsData?.formatted_delivered_on}
                  deliverBy={orderDetailsData?.deliver_by_formatted}
                  pickedUpOn={orderDetailsData?.delivered_on_formatted}
                  pickupBy={orderDetailsData.pickup_by_formatted}
                  servicedOn={orderDetailsData?.formatted_delivered_on}
                  serviceBy={orderDetailsData?.service_by_formatted}
                  mixOrderConfiguration={orderDetailsData?.orders?.mix_order_configuration}
                  scheduledForStart={orderDetailsData?.orders?.scheduled_start_date_formatted}
                  scheduledForEnd={orderDetailsData?.orders?.scheduled_end_date_formatted}
                />
                {orderDetailsData?.is_back_dated && (
                  <Tooltip title="Order was backdated when completed.">
                    <EventAvailableRoundedIcon style={{ color: "var(--color-orange" }} />
                  </Tooltip>
                )}
              </Stack>
              {orderDetailsData?.cancellation_info?.formatted_canceled_on && (
                <ItemLabel label="Canceled on" text={orderDetailsData?.cancellation_info?.formatted_canceled_on} />
              )}
              {orderDetailsData?.cancellation_info?.canceled_by && (
                <ItemLabel label="Canceled by" text={orderDetailsData?.cancellation_info?.canceled_by} />
              )}
              {orderDetailsData?.cancellation_info?.canceled_reason && (
                <ItemLabel label="Canceled reason" text={orderDetailsData?.cancellation_info?.canceled_reason} />
              )}
              {orderDetailsData?.signature?.signature_svg && (
                <ItemLabel
                  label="Signature"
                  text={<img src={orderDetailsData.signature.signature_svg} alt="Signature" style={{ maxWidth: "100px", maxHeight: "100px" }} />}
                />
              )}
            </Stack>
          </Panel>
          {proofOfDelivery && Object.keys(proofOfDelivery).map((key) => {
            if (proofOfDelivery[key]) {
              return (
                <ProofOrderDetailCards
                  key={key}
                  proofData={proofOfDelivery[key]}
                  orderDetailsData={orderDetailsData}
                  setProofData={setProofOfDelivery}
                />
              );
            }
            return null;
          })}
          {proofOfDelivery?.order_complete_status == "Complete" && (
            <Box className={styles.buttonContainer}>
              <Button
                size="small"
                color="primary">
                Share feedback
              </Button>
            </Box>
          )}
          {((orderDetailsData?.orders?.order_complete_status == "Delivered" || orderDetailsData?.orders?.order_complete_status == "Picked Up") && (checkOrderFeedbackExpired(orderDetailsData?.delivered_on, orderFeedBackExpirationDays) && (!orderFeedbackData?.is_form_submitted && !orderFeedbackData?.is_link_expired)) && surveyLinkWithAuthForUser) &&
            <div className="textAlignEnd fontWeight600 marginBottom10px">
              <Link to={{ pathname: `/orders/feedback-order` }} state={{ orderFeedbackId: orderDetailsData?.orders?.order_id }} className={styles.customLink}>{t("SupportComponent.share_feedback")}</Link>
            </div>
          }
        </Stack> :
        isLoading && <OrdersDetailsSkeleton />
      }
      <CancelOrder
        isOpen={isCancelOrderPopupOpen}
        handleCancel={() => setIsCancelOrderPopupOpen(false)}
        handleSubmit={handleCancelOrder}
        orderName={alertEquipmentName}
        cancelReason={cancelReason}
        setCancelReason={setCancelReason}
        contentText={`Please enter the reason for canceling this order:`}
        contentText2={`Reason`}
        headerText="Cancel Order"
        cancelText="Go back"
        okText="Cancel order"
      />
    </>
  );
};

export default withTitle(OrdersDetails, "Order Details");
